import { formatDateTime, formatFileSize } from '@brimo/shared';
import { Button, Eyebrow, Pill } from '../../ds/components';
import { statusPill } from './SelfDisclosurePage';
import type { FullView } from './api';
import styles from './SelfDisclosurePage.module.css';

export interface ViewTabProps {
  full: FullView;
  reviewing: boolean;
  onReview: () => void;
}

/** Selbstauskunft 08 „Ansicht": all groups as label/value rows, proofs, signature, event log, PDF, review. */
export function ViewTab({ full, reviewing, onReview }: ViewTabProps) {
  const pill = statusPill(full);
  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardHeadText}>
            <div className={styles.cardTitle}>Selbstauskunft</div>
            <div className={styles.cardSubtitle}>{full.headline}</div>
          </div>
          <div className={styles.cardHeadActions}>
            <Pill variant={pill.variant}>{pill.label}</Pill>
            {full.pdfFileId ? (
              <a
                href={`/api/files/${full.pdfFileId}`}
                target="_blank"
                rel="noreferrer"
                className={styles.smallButton}
              >
                PDF
              </a>
            ) : null}
            {full.canMarkReviewed ? (
              <Button variant="secondary" size="small" loading={reviewing} onClick={onReview}>
                Als geprüft markieren
              </Button>
            ) : null}
          </div>
        </div>
        <div className={styles.groups}>
          {full.groups.map((group) => (
            <div key={group.key}>
              <Eyebrow as="div" className={styles.groupLabel}>
                {group.title}
              </Eyebrow>
              {group.rows.map((row) => (
                <div key={row.field} className={styles.row}>
                  <span className={styles.rowLabel}>{row.label}</span>
                  <span className={styles.rowValue}>{row.value}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        {full.signature ? (
          <div className={styles.signatureBlock}>
            <Eyebrow as="div" className={styles.groupLabel}>
              Unterschrift
            </Eyebrow>
            <div className={styles.signatureRow}>
              {full.signature.fileId ? (
                <img
                  src={`/api/files/${full.signature.fileId}`}
                  alt={`Unterschrift von ${full.contact.name}`}
                  className={styles.signatureImage}
                />
              ) : null}
              <span className={styles.signatureMeta}>
                {full.contact.name}
                {full.signature.place ? ` · ${full.signature.place}` : ''},{' '}
                {formatDateTime(full.signature.signedAt)}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {full.proofs.length > 0 ? (
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardHeadText}>
              <div className={styles.cardTitle}>Nachweise</div>
              <div className={styles.cardSubtitle}>
                {full.proofs.length} {full.proofs.length === 1 ? 'Datei' : 'Dateien'} · vom Kunden hochgeladen
              </div>
            </div>
          </div>
          <div className={styles.list}>
            {full.proofs.map((proof) => (
              <div key={proof.id} className={styles.listRow}>
                <span className={styles.listText}>
                  <span className={styles.listTitle}>
                    {proof.typeLabel} · {proof.fileName}
                  </span>
                  <span className={styles.listMeta}>
                    {formatFileSize(proof.size)} · {formatDateTime(proof.uploadedAt)}
                    {proof.scanStatus === 'pending' ? ' · wird geprüft' : ''}
                  </span>
                </span>
                <Pill variant={proof.status === 'reviewed' ? 'success' : 'neutral'}>{proof.statusLabel}</Pill>
                {proof.canOpen ? (
                  <a
                    href={`/api/files/${proof.fileId}`}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.smallButton}
                  >
                    Öffnen
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardHeadText}>
            <div className={styles.cardTitle}>Verlauf</div>
            <div className={styles.cardSubtitle}>Versand, Bearbeitung und Prüfung dieser Version</div>
          </div>
        </div>
        {full.events.length === 0 ? (
          <p className={styles.cardNote}>Noch keine Ereignisse.</p>
        ) : (
          <ol className={styles.events}>
            {full.events.map((event, index) => (
              <li key={`${event.at}-${index}`} className={styles.event}>
                <span className={styles.eventDot} aria-hidden="true" />
                <span className={styles.eventText}>
                  <span className={styles.eventTitle}>{event.title}</span>
                  <span className={styles.eventMeta}>
                    {formatDateTime(event.at)} · {event.by}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </>
  );
}
